import parse from 'date-fns/parse'
import { Action, Dispatch } from 'redux'
import { State } from 'index'
import { push } from 'connected-react-router'
import { db, FirebaseTimestamp, FirebaseWriteBatch } from 'firebase/index'
import { dateToString, parseDateWithHyphen, parseTimeToDate, parseTimeToNumber } from 'lib/helper'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { setStoreCalendarsAction } from 'reducks/storeCalendar/actions'
import { StockType } from 'types/stock'
import { StoreCalendarType } from 'types/storeCalendar'
import { StoreTimesale } from 'types/storeTimesale'
import { fetchStoreTimesaleAction } from 'reducks/storeTimesale/actions'
import { isBefore } from 'date-fns'

const saveStoreCalendars = (
  storeCalendars: StoreCalendarType[],
  deletedStocks: StockType[],
  page: 'close' | 'open'
) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    dispatch(showLoadingAction())
    storeCalendars.sort((a, b) => (a.date < b.date ? -1 : 1))
    const storeRef = getState().auth.store_ref
    const stocks = getState().stock.stocks
    const storeTimesale: StoreTimesale = getState().storeTimesale.storeTimesale
    const calendarsRef = storeRef.collection('store_calendars')
    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0
    for (let i = 0; i < storeCalendars.length; i = (i + 1) | 0) {
      const storeCalendar = storeCalendars[i]
      const { date, open_time, last_order_time } = storeCalendar
      // ステータスがブランクであれば削除
      if (storeCalendar.status === '') {
        const storeCalendarRef = calendarsRef.doc(`calendar_${storeRef.id}-${date}`)
        batchArray[batchIndex].delete(storeCalendarRef)
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      } else {
        const storeCalendarRef = calendarsRef.doc(`calendar_${storeRef.id}-${date}`)
        batchArray[batchIndex].set(storeCalendarRef, storeCalendar, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
        // 臨時営業日の場合は条件に一致する在庫を変更する
        if (storeCalendar.status === 'open') {
          const prevStocks: StockType[] = []
          for (const stock of stocks) {
            const isDailyStock = stock.date === date && stock.type === 'daily' && !!last_order_time
            // 当日取り置きの在庫は注文締切を設定する
            if (isDailyStock) {
              stock.deadline = FirebaseTimestamp.fromDate(
                parse(`${parseDateWithHyphen(stock.date)} ${last_order_time}`, 'yyyy-MM-dd HH:mm', new Date())
              )
            }
            // 受渡解禁時刻が変更後の店舗オープン時刻より早い在庫の検証
            if (
              stock.date === date &&
              !!open_time &&
              parseTimeToNumber(stock.pickupable_time) <= parseTimeToNumber(open_time)
            ) {
              const prevStockId = stock.stock_ref.id
              const nextStockId = prevStockId.slice(0, -5) + open_time // stock_idの末尾5桁を受渡解禁時刻に合わせる
              // 受渡解禁時刻がマージ対象か判定する
              const index = prevStocks.findIndex((prevStock: StockType) => {
                return (
                  parseTimeToNumber(prevStock.pickupable_time) <= parseTimeToNumber(stock.pickupable_time) &&
                  prevStock.bread_ref.id === stock.bread_ref.id &&
                  prevStock.date === stock.date
                )
              })
              // idが異なれば古いstockを削除
              if (index === -1) {
                const { stock_ref, ...rest } = stock
                batchArray[batchIndex].delete(stock_ref)
                operationCounter++
                if (operationCounter === 499) {
                  batchArray.push(db.batch())
                  batchIndex++
                  operationCounter = 0
                }
                const nextStock: StockType = {
                  ...rest,
                  pickupable_time: open_time,
                }
                const stockRef = storeRef.collection('store_stocks').doc(nextStockId)
                batchArray[batchIndex].set(stockRef, nextStock, { merge: true })
                operationCounter++
                if (operationCounter === 499) {
                  batchArray.push(db.batch())
                  batchIndex++
                  operationCounter = 0
                }
              } else {
                // 受渡解禁時刻のマージ
                const stockRef = storeRef.collection('store_stocks').doc(nextStockId)
                const nextStock: StockType = {
                  ...stock,
                  stock: prevStocks[index].stock + stock.stock,
                }
                batchArray[batchIndex].set(stockRef, nextStock, { merge: true })
                operationCounter++
                if (operationCounter === 499) {
                  batchArray.push(db.batch())
                  batchIndex++
                  operationCounter = 0
                }
              }
            } else if (isDailyStock) {
              // 当日取り置きの在庫は注文締切を変更する
              const { stock_ref, ...rest } = stock
              batchArray[batchIndex].set(stock_ref, rest, { merge: true })
              operationCounter++
              if (operationCounter === 499) {
                batchArray.push(db.batch())
                batchIndex++
                operationCounter = 0
              }
            }
            // 処理済みの在庫を格納
            prevStocks.push(stock)
          }

          // 本日の臨時営業日のラストオーダー時刻がタイムセールの終了時刻より前であればタイムセールの終了時刻を変更する
          if (
            storeTimesale &&
            date === dateToString(new Date()) &&
            storeTimesale.is_active &&
            !!last_order_time &&
            isBefore(parseTimeToDate(last_order_time), storeTimesale.end_at.toDate())
          ) {
            const nextStoreTimesale: StoreTimesale = {
              ...storeTimesale,
              end_at: FirebaseTimestamp.fromDate(
                parse(`${parseDateWithHyphen(date)} ${last_order_time}`, 'yyyy-MM-dd HH:mm', new Date())
              ),
              updated_at: FirebaseTimestamp.now(),
            }
            const { ref, ...rest } = nextStoreTimesale
            if (!ref) return
            dispatch(fetchStoreTimesaleAction(nextStoreTimesale))
            batchArray[batchIndex].set(ref, rest, { merge: true })
            operationCounter++
            if (operationCounter === 499) {
              batchArray.push(db.batch())
              batchIndex++
              operationCounter = 0
            }
          }
        }
      }
    }

    // 削除対象の在庫
    for (let i = 0; i < deletedStocks.length; i = (i + 1) | 0) {
      const deletedStock = deletedStocks[i]
      const { stock_ref } = deletedStock
      if (!stock_ref) continue
      batchArray[batchIndex].delete(stock_ref)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    await batchArray.forEach(async (batch) => await batch.commit())
    dispatch(setStoreCalendarsAction(storeCalendars))
    dispatch(hideLoadingAction())
    dispatch(push(`/account/settings/${page}/complete`))
  }
}

export default saveStoreCalendars
